import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Alert, Badge, Button, Card, Col, Form, Row, Spinner,
} from 'react-bootstrap';
import BillingoService from '../../../Services/BillingoService';
import StaleApiNotice from '../../../Components/Billingo/StaleApiNotice';
import { formatAmount } from '../../../Utils/billingoDocuments';
import { formatTripWhen, routeLabel, payerLabel } from '../../../Utils/bookingContext';
import { countActiveFilters } from './InvoiceFilters';
import BarList from './BarList';
import { compactAmounts, fullAmounts } from './moneySummary';
import ListPager from '../../../Components/ListPager';
import InvoicingKpi from './InvoicingKpi';
import MissingDocumentsFilters, {
  AGE_LABELS, EMPTY_MISSING_FILTERS, MISSING_KIND_LABELS,
} from './MissingDocumentsFilters';
import useDocumentIssuer from '../../../Components/Billingo/useDocumentIssuer';
import useHideBookings from './useHideBookings';
import useBookingDocuments from './useBookingDocuments';
import BookingDocumentsLine from './BookingDocumentsLine';
import useBookingDetails from './useBookingDetails';
import AskAIButton from '../../../Components/ChatWidget/AskAIButton';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// The partner chart shows the biggest few; the rest fold into one row rather
// than a list nobody reads.
const TOP_PARTNERS = 5;

// Monday of the row's (earliest) trip week, as YYYY-MM-DD.
export const weekOf = (ymd) => {
  if (!ymd) return '';
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  const offset = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - offset);
  return d.toISOString().slice(0, 10);
};

const weekLabel = (monday) => {
  if (!monday) return 'No trip date';
  const d = new Date(`${monday}T00:00:00Z`);
  return `Week of ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

function DateTile({ ymd }) {
  if (!ymd) return <div className="missing-date-tile text-muted">—</div>;
  const d = new Date(`${ymd.slice(0, 10)}T00:00:00Z`);
  return (
    <div className="missing-date-tile" aria-hidden="true">
      <div className="missing-date-day">{d.getUTCDate()}</div>
      <div className="missing-date-month">{MONTHS[d.getUTCMonth()]}</div>
    </div>
  );
}

/**
 * Missing documents — the backlog of past Confirmed/Completed bookings (since
 * Billingo went live, monthly-billed partners excluded) with no document at
 * all. Unlike the final-Számla queue this is a list to triage, so it leads
 * with its shape: how many, how much money, how old, whose. The charts are the
 * filters; the list below is grouped by trip week with multi-select, so a
 * whole chunk can be hidden (invoiced elsewhere, not owed a document) or put on
 * one document at once. The Hidden view lists every hidden booking, of either
 * kind, for unhiding.
 */
function MissingDocuments({ refreshKey = 0, onSummaryChange = () => {}, onChanged = () => {} }) {
  const [filters, setFilters] = useState({ ...EMPTY_MISSING_FILTERS });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  // Typing in the search box shouldn't fire a request per keystroke.
  const [debouncedQ, setDebouncedQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(filters.q), 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const next = await BillingoService.listMissingDocuments({
        ...filters,
        q: debouncedQ,
        // The Hidden view lists hidden bookings of either kind for unhiding.
        missing: filters.hidden ? '' : 'no_documents',
        hidden: filters.hidden ? 'true' : '',
        page,
        limit,
      });
      setResult(next);
      setSelected(new Set());
      if (next.summary) onSummaryChange(next.summary);
    } catch (err) {
      // An empty list would read as "nothing missing" — the one wrong answer.
      setResult(null);
      setLoadError(err.response?.data?.message || err.message || 'Could not load missing documents.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [debouncedQ, page, limit, refreshKey,
    filters.status, filters.age, filters.partner_id, filters.event_name,
    filters.from, filters.to, filters.hidden]);

  useEffect(() => { load(); }, [load]);

  const reloadAll = () => { load(); onChanged(); };
  const {
    issue, preparing, error, modal,
  } = useDocumentIssuer({ onIssued: reloadAll });
  const hider = useHideBookings({ onChanged: load });
  const { busy } = hider;

  // Any filter change restarts at page 1 — staying on page 7 of a narrower
  // result set would show an empty list.
  const handleFiltersChange = (next) => {
    setFilters(next);
    setPage(1);
  };
  const setFilter = (field, value) => handleFiltersChange({ ...filters, [field]: value });

  const rows = useMemo(() => result?.rows || [], [result]);
  const docs = useBookingDocuments(rows);
  const details = useBookingDetails(rows, reloadAll);
  const stats = result?.stats;
  const facets = result?.facets;
  const summary = result?.summary;

  const weeks = useMemo(() => {
    const byWeek = new Map();
    rows.forEach((row) => {
      const key = weekOf(row.date);
      byWeek.set(key, [...(byWeek.get(key) || []), row]);
    });
    return [...byWeek.entries()];
  }, [rows]);

  const toggle = (keys, on) => setSelected((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => (on ? next.add(k) : next.delete(k)));
    return next;
  });

  const selectedRows = rows.filter((r) => selected.has(r.group_key));

  const setHidden = (groups, hidden) => (hidden ? hider.hide(groups) : hider.unhide(groups));

  // One document for several bookings — the flow's review step still shows
  // every line before anything is issued.
  const issueSelection = () => issue({
    group_key: 'selection',
    booking_ids: selectedRows.flatMap((r) => r.booking_ids),
  });

  const ageItems = (stats?.by_age || []).map((a) => ({
    key: a.bucket, label: AGE_LABELS[a.bucket], count: a.count,
  }));
  const partnerItems = useMemo(() => {
    const all = stats?.by_partner || [];
    const top = all.slice(0, TOP_PARTNERS)
      .map((p) => ({ key: p.id, label: p.name, count: p.count }));
    const rest = all.slice(TOP_PARTNERS);
    if (rest.length) {
      top.push({
        key: '__other',
        label: `${rest.length} other partner${rest.length > 1 ? 's' : ''}`,
        count: rest.reduce((sum, p) => sum + p.count, 0),
        disabled: true,
      });
    }
    return top;
  }, [stats]);

  const filtered = countActiveFilters(filters) > 0 || !!filters.q;
  let emptyMessage = 'Every past confirmed or completed booking has a document.';
  if (filters.hidden) emptyMessage = 'No hidden bookings.';
  else if (filtered) emptyMessage = 'No bookings match your filters.';

  const shownError = error || hider.error || loadError;

  return (
    <div>
      <div className="mb-3">
        <h6 className="mb-1">{filters.hidden ? 'Hidden bookings' : 'Bookings with no documents'}</h6>
        <div className="text-muted small">
          Past confirmed or completed bookings since Billingo invoicing went live
          (27 Jun 2026), excluding partners billed monthly. Completed trips that
          already have a document are in Needs final Számla.
        </div>
      </div>

      {stats && (
        <Row className="g-3 mb-3">
          <Col xs={12} sm={6} lg={3}>
            <InvoicingKpi
              label={filters.hidden ? 'Hidden' : 'With no documents'}
              value={stats.count}
              icon={filters.hidden ? 'fa-eye-slash' : 'fa-file-circle-exclamation'}
              accent="#dc2626"
            />
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <InvoicingKpi
              label="Not invoiced"
              value={compactAmounts(stats.remaining) || '—'}
              title={fullAmounts(stats.remaining)}
              icon="fa-coins"
              accent="#d97706"
            />
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <InvoicingKpi
              label="Completed · Confirmed"
              value={`${stats.by_status.Completed} · ${stats.by_status.Confirmed}`}
              detail="trip rendered · trip not yet marked done"
              icon="fa-route"
              accent="#2563eb"
            />
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <InvoicingKpi
              label={filters.hidden ? 'Back to the backlog' : 'Hidden by the team'}
              value={filters.hidden ? (summary?.no_documents ?? '—') : (summary?.hidden ?? 0)}
              detail={filters.hidden ? 'bookings still to chase' : 'click to review or unhide'}
              icon={filters.hidden ? 'fa-list-check' : 'fa-eye-slash'}
              accent="#6b7280"
              active={filters.hidden}
              onClick={() => setFilter('hidden', !filters.hidden)}
            />
          </Col>
        </Row>
      )}

      {stats && stats.count > 0 && (
        <Card className="border-0 shadow-sm mb-3">
          <Card.Body>
            <Row className="g-4">
              <Col xs={12} md={6}>
                <BarList
                  title="Trip age"
                  items={ageItems}
                  selected={filters.age}
                  onSelect={(v) => setFilter('age', v)}
                />
              </Col>
              <Col xs={12} md={6}>
                <BarList
                  title="By partner"
                  items={partnerItems}
                  selected={filters.partner_id}
                  onSelect={(v) => setFilter('partner_id', v)}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <MissingDocumentsFilters
        filters={filters}
        onChange={handleFiltersChange}
        facets={facets}
        open={filtersOpen}
        onToggle={() => setFiltersOpen((o) => !o)}
      />

      {shownError && <Alert variant="danger" className="py-2 small">{shownError}</Alert>}

      <StaleApiNotice show={!!result?.stale} detail="the Missing documents list" />

      {loading && (
        <div className="text-muted small mb-2">
          <Spinner size="sm" animation="border" className="me-2" />
          Loading…
        </div>
      )}

      {!loading && !loadError && result && !result.stale && rows.length === 0 && (
        <div className="text-center text-muted py-5">
          <i className="fa fa-circle-check fa-2x mb-2 d-block text-success opacity-75" />
          {emptyMessage}
        </div>
      )}

      {selected.size > 0 && (
        <div className="missing-bulk-bar" role="region" aria-label="Selected bookings">
          <span className="fw-semibold">{`${selected.size} selected`}</span>
          <div className="d-flex flex-wrap gap-2 ms-auto">
            {!filters.hidden && (
              <Button
                size="sm"
                variant="light"
                onClick={issueSelection}
                disabled={busy || preparing === 'selection'}
              >
                <i className="fa fa-file-invoice me-1" />
                {preparing === 'selection' ? 'Opening…' : 'Create one document'}
              </Button>
            )}
            <Button
              size="sm"
              variant="light"
              onClick={() => setHidden(selectedRows, !filters.hidden)}
              disabled={busy}
            >
              <i className={`fa ${filters.hidden ? 'fa-eye' : 'fa-eye-slash'} me-1`} />
              {filters.hidden ? `Unhide ${selected.size}` : `Hide ${selected.size}`}
            </Button>
            <Button size="sm" variant="link" className="text-white" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {weeks.map(([week, weekRows]) => {
        const keys = weekRows.map((r) => r.group_key);
        const allOn = keys.every((k) => selected.has(k));
        return (
          <section key={week || 'undated'} className="mb-3" aria-label={weekLabel(week)}>
            <div className="missing-week-header">
              <Form.Check
                type="checkbox"
                id={`missing-week-${week || 'undated'}`}
                checked={allOn}
                onChange={(e) => toggle(keys, e.target.checked)}
                aria-label={`Select ${weekLabel(week)}`}
                className="mb-0"
              />
              <span className="fw-semibold">{weekLabel(week)}</span>
              <span className="text-muted small">{`${weekRows.length} booking${weekRows.length > 1 ? 's' : ''}`}</span>
            </div>

            {weekRows.map((row) => {
              const [first] = row.bookings;
              const isSelected = selected.has(row.group_key);
              return (
                <div key={row.group_key} className={`missing-row${isSelected ? ' is-selected' : ''}`}>
                  <div className="missing-row-main">
                    <Form.Check
                      type="checkbox"
                      id={`missing-row-${row.group_key}`}
                      checked={isSelected}
                      onChange={(e) => toggle([row.group_key], e.target.checked)}
                      aria-label={`Select ${first.booking_reference}`}
                      className="mb-0"
                    />
                    <DateTile ymd={row.date} />
                    <div className="missing-row-body">
                      <button
                        type="button"
                        className="missing-row-title"
                        onClick={() => details.open(row)}
                        title="Open the booking's details"
                      >
                        <span className="fw-semibold">{first.booking_reference}</span>
                        {first.customer_name && <span>{` · ${first.customer_name}`}</span>}
                        {row.bookings.length > 1 && (
                          <Badge bg="primary" className="ms-2 fw-normal">{`+${row.bookings.length - 1} more`}</Badge>
                        )}
                      </button>
                      <div className="text-muted small text-truncate" title={routeLabel(first, 200)}>
                        {`${formatTripWhen(first.date)} · ${routeLabel(first)}`}
                      </div>
                      <div className="d-flex flex-wrap gap-1 mt-1">
                        <Badge bg="light" text="dark" className="fw-normal border">
                          {payerLabel(row)}
                        </Badge>
                        <Badge
                          bg={first.status === 'Completed' ? 'success-subtle' : 'secondary-subtle'}
                          text={first.status === 'Completed' ? 'success-emphasis' : 'secondary-emphasis'}
                          className="fw-normal"
                        >
                          {first.status}
                        </Badge>
                        {filters.hidden && (
                          <Badge bg="warning-subtle" text="warning-emphasis" className="fw-normal">
                            {MISSING_KIND_LABELS[row.missing] || row.missing}
                          </Badge>
                        )}
                        {first.event_name && (
                          <Badge bg="info-subtle" text="info-emphasis" className="fw-normal">{first.event_name}</Badge>
                        )}
                      </div>
                      <BookingDocumentsLine row={row} docs={docs} />
                      {row.hidden && (
                        <div className="text-muted small mt-1">
                          {[`Hidden${row.hidden_by ? ` by ${row.hidden_by}` : ''}`,
                            formatTripWhen(row.hidden_at, { withTime: false })].filter(Boolean).join(' · ')}
                          {': '}
                          {row.hidden_reason
                            ? <span className="fst-italic">{row.hidden_reason}</span>
                            : <span className="fst-italic">no reason given</span>}
                        </div>
                      )}
                    </div>
                    <div className="missing-row-money">
                      <div className="fw-semibold">{formatAmount(row.booking_value, row.currency)}</div>
                      <div className={`small ${row.paid + 0.005 >= row.booking_value ? 'text-success' : 'text-muted'}`}>
                        {row.paid + 0.005 >= row.booking_value ? 'paid' : `${formatAmount(row.paid, row.currency)} paid`}
                      </div>
                    </div>
                    <div className="missing-row-actions">
                      <AskAIButton refs={row.bookings.map((b) => b.booking_reference)} />
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => issue(row)}
                        disabled={preparing === row.group_key}
                      >
                        {preparing === row.group_key ? 'Opening…' : 'Create document'}
                      </Button>
                      <Button
                        size="sm"
                        variant="link"
                        className="text-muted"
                        onClick={() => setHidden([row], !row.hidden)}
                        disabled={busy}
                        title={row.hidden ? 'Show this again in Missing documents' : 'Hide for everyone'}
                        aria-label={`${row.hidden ? 'Unhide' : 'Hide'} ${first.booking_reference}`}
                      >
                        <i className={`fa ${row.hidden ? 'fa-eye' : 'fa-eye-slash'}`} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
        );
      })}

      {result?.pagination && (
        <ListPager
          pagination={result.pagination}
          onPageChange={setPage}
          onLimitChange={(n) => { setLimit(n); setPage(1); }}
        />
      )}

      {modal}
      {hider.modal}
      {details.modal}
    </div>
  );
}

export default MissingDocuments;
